// src/pages/student/StudentTeamsPage.js
import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    CardContent,
    Chip,
    Stack,
    Alert,
    CircularProgress,
    Container,
    Divider,
    Avatar,
    IconButton,
    Tooltip,
    Paper,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    CardActionArea,
} from "@mui/material";
import {
    Add as AddIcon,
    GroupAdd as GroupAddIcon,
    ContentCopy as CopyIcon,
    People as PeopleIcon,
    EmojiEvents as TrophyIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    School as SchoolIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import teamService from "../../services/teamService";
import { getDocumentById } from "../../firebase/firestore";
import AdminLayout from "../AdminLayout";

export default function StudentTeamsPage() {
    const { user, userProfile } = useAuth();
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [joinOpen, setJoinOpen] = useState(false);
    const [teamName, setTeamName] = useState("");
    const [joinCode, setJoinCode] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [copiedCode, setCopiedCode] = useState(null);

    // Team Members Dialog State
    const [membersDialogOpen, setMembersDialogOpen] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);

    const canCreate = userProfile?.role === "student";

    const loadTeams = async () => {
        try {
            setLoading(true);
            setError("");
            const data = await teamService.getTeamsByUser(user?.uid);
            setTeams(data || []);
        } catch (e) {
            setError(e.message || "Failed to load teams");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.uid) {
            loadTeams();
        }
    }, [user?.uid]);

    const handleCreateTeam = async () => {
        try {
            setError("");
            setSuccess("");
            if (!teamName.trim()) {
                setError("Team name is required");
                return;
            }

            const created = await teamService.createStudentTeam(user.uid, teamName);
            setTeamName("");
            setCreateOpen(false);
            setSuccess(`🎉 Team created successfully! Team code: ${created.teamCode}`);
            await loadTeams();
        } catch (e) {
            setError(e.message || "Failed to create team");
        }
    };

    const handleJoinTeam = async () => {
        try {
            setError("");
            setSuccess("");
            if (!joinCode.trim()) {
                setError("Team code is required");
                return;
            }

            const joined = await teamService.joinTeamByCode(user.uid, joinCode);
            setJoinCode("");
            setJoinOpen(false);
            setSuccess(`🎉 Successfully joined team: ${joined.teamName}`);
            await loadTeams();
        } catch (e) {
            setError(e.message || "Failed to join team");
        }
    };

    const handleCopyCode = (code) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
    };

    // ✅ NEW: Handle team card click to view members
    const handleTeamClick = async (team) => {
        try {
            setSelectedTeam(team);
            setMembersDialogOpen(true);
            setLoadingMembers(true);

            // Fetch all member details
            const memberIds = Array.isArray(team.members) ? team.members : [];
            const memberDetails = await Promise.all(
                memberIds.map(async (memberId) => {
                    try {
                        const userData = await getDocumentById("users", memberId);
                        return userData || { id: memberId, firstName: "Unknown", lastName: "User" };
                    } catch (error) {
                        // console.error(`Error fetching user ${memberId}:`, error);
                        return { id: memberId, firstName: "Unknown", lastName: "User" };
                    }
                })
            );

            setTeamMembers(memberDetails);
        } catch (error) {
            console.error("Error loading team members:", error);
            setError("Failed to load team members");
        } finally {
            setLoadingMembers(false);
        }
    };

    const handleCloseMembersDialog = () => {
        setMembersDialogOpen(false);
        setSelectedTeam(null);
        setTeamMembers([]);
    };

    if (loading) {
        return (
            <AdminLayout title="My Teams">
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "60vh",
                    }}
                >
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="My Teams">
            <Container maxWidth="lg" sx={{ py: 3 }}>
                {/* Header Section */}
                <Box sx={{ mb: 4 }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                🤝 My Teams
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Create or join teams to collaborate on competitions, events, and
                                projects.
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={2}>
                            {canCreate && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setCreateOpen(true)}
                                    sx={{
                                        backgroundColor: "#003366",
                                        "&:hover": { backgroundColor: "#002244" },
                                        textTransform: "capitalize",
                                        fontWeight: 600,
                                    }}
                                >
                                    Create Team
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                startIcon={<GroupAddIcon />}
                                onClick={() => setJoinOpen(true)}
                                sx={{
                                    backgroundColor: "#C1272D",
                                    "&:hover": { backgroundColor: "#a01f25" },
                                    textTransform: "capitalize",
                                    fontWeight: 600,
                                }}
                            >
                                Join Team
                            </Button>
                        </Stack>
                    </Box>

                    {/* Alerts */}
                    {error && (
                        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" onClose={() => setSuccess("")} sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}
                </Box>

                {/* Teams Grid */}
                {teams.length === 0 ? (
                    <Card
                        sx={{
                            textAlign: "center",
                            py: 8,
                            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                        }}
                    >
                        <PeopleIcon sx={{ fontSize: 80, color: "#003366", mb: 2 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            No Teams Yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Create your own team or join an existing one to get started.
                        </Typography>
                        <Stack direction="row" spacing={2} justifyContent="center">
                            {canCreate && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setCreateOpen(true)}
                                    sx={{
                                        backgroundColor: "#003366",
                                        "&:hover": { backgroundColor: "#002244" },
                                    }}
                                >
                                    Create Team
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                startIcon={<GroupAddIcon />}
                                onClick={() => setJoinOpen(true)}
                                sx={{
                                    borderColor: "#C1272D",
                                    color: "#C1272D",
                                    "&:hover": {
                                        borderColor: "#a01f25",
                                        backgroundColor: "rgba(193, 39, 45, 0.04)",
                                    },
                                }}
                            >
                                Join Team
                            </Button>
                        </Stack>
                    </Card>
                ) : (
                    <Grid container spacing={3}>
                        {teams.map((team) => {
                            const isLeader = team.leaderId === user?.uid;
                            const memberCount = Array.isArray(team.members)
                                ? team.members.length
                                : 0;

                            return (
                                <Grid item xs={12} md={6} key={team.id}>
                                    <Card
                                        sx={{
                                            height: "100%",
                                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                            "&:hover": {
                                                transform: "translateY(-8px)",
                                                boxShadow: 6,
                                                cursor: "pointer",
                                            },
                                            position: "relative",
                                            overflow: "visible",
                                        }}
                                    >
                                        {/* ✅ Make card clickable */}
                                        <CardActionArea onClick={() => handleTeamClick(team)}>
                                            {/* Leader Badge */}
                                            {isLeader && (
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        top: -10,
                                                        right: 16,
                                                        background:
                                                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                                        color: "white",
                                                        px: 2,
                                                        py: 0.5,
                                                        borderRadius: 2,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        boxShadow: 3,
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <TrophyIcon sx={{ fontSize: 16 }} />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ fontWeight: 700 }}
                                                    >
                                                        Team Leader
                                                    </Typography>
                                                </Box>
                                            )}

                                            <CardContent sx={{ p: 3 }}>
                                                {/* Team Name & Code */}
                                                <Box sx={{ mb: 3 }}>
                                                    <Typography
                                                        variant="h5"
                                                        sx={{
                                                            fontWeight: 700,
                                                            mb: 1,
                                                            color: "#003366",
                                                        }}
                                                    >
                                                        {team.teamName}
                                                    </Typography>
                                                    {team.teamCode && (
                                                        <Box
                                                            sx={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                                backgroundColor: "#f5f5f5",
                                                                px: 2,
                                                                py: 1,
                                                                borderRadius: 2,
                                                                border: "2px dashed #C1272D",
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontFamily: "monospace",
                                                                    fontWeight: 700,
                                                                    fontSize: "1rem",
                                                                    color: "#C1272D",
                                                                }}
                                                            >
                                                                {team.teamCode}
                                                            </Typography>
                                                            <Tooltip
                                                                title={
                                                                    copiedCode === team.teamCode
                                                                        ? "Copied!"
                                                                        : "Copy Code"
                                                                }
                                                            >
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleCopyCode(team.teamCode);
                                                                    }}
                                                                    sx={{
                                                                        color: "#C1272D",
                                                                        "&:hover": {
                                                                            backgroundColor:
                                                                                "rgba(193, 39, 45, 0.1)",
                                                                        },
                                                                    }}
                                                                >
                                                                    <CopyIcon sx={{ fontSize: 18 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    )}
                                                </Box>

                                                <Divider sx={{ mb: 2 }} />

                                                {/* Team Info */}
                                                <Stack spacing={2}>
                                                    {/* Leader Info */}
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1.5,
                                                        }}
                                                    >
                                                        <Avatar
                                                            sx={{
                                                                width: 40,
                                                                height: 40,
                                                                backgroundColor: "#003366",
                                                            }}
                                                        >
                                                            <PersonIcon />
                                                        </Avatar>
                                                        <Box sx={{ flexGrow: 1 }}>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                Team Leader
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ fontWeight: 600 }}
                                                            >
                                                                {team.leaderName ||
                                                                    (isLeader ? "You" : team.leaderId)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    {/* Email */}
                                                    {team.leaderEmail && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <EmailIcon
                                                                sx={{
                                                                    fontSize: 18,
                                                                    color: "text.secondary",
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                {team.leaderEmail}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {/* College */}
                                                    {team.leaderCollege && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <SchoolIcon
                                                                sx={{
                                                                    fontSize: 18,
                                                                    color: "text.secondary",
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                {team.leaderCollege}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Stack>

                                                <Divider sx={{ my: 2 }} />

                                                {/* Member Count */}
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <PeopleIcon sx={{ color: "#003366" }} />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ fontWeight: 600 }}
                                                        >
                                                            {memberCount}{" "}
                                                            {memberCount === 1 ? "Member" : "Members"}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        label={isLeader ? "Leader" : "Member"}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: isLeader
                                                                ? "#C1272D"
                                                                : "#003366",
                                                            color: "white",
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </Box>

                                                {/* ✅ Click to view hint */}
                                                <Box
                                                    sx={{
                                                        mt: 2,
                                                        pt: 2,
                                                        borderTop: "1px dashed #e0e0e0",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: "#003366",
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        👆 Click to view all members
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}

                {/* Create Team Dialog */}
                <Dialog
                    open={createOpen}
                    onClose={() => setCreateOpen(false)}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle sx={{ fontWeight: 700, fontSize: "1.5rem" }}>
                        🚀 Create New Team
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                label="Team Name"
                                placeholder="Enter a unique team name"
                                value={teamName}
                                onChange={(e) => setTeamName(e.target.value)}
                                autoFocus
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        "&.Mui-focused fieldset": {
                                            borderColor: "#003366",
                                        },
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: "#003366",
                                    },
                                }}
                            />
                            <Alert severity="info" sx={{ mt: 2 }}>
                                After creating the team, you'll receive a unique team code to share
                                with members.
                            </Alert>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setCreateOpen(false)} sx={{ color: "#666" }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateTeam}
                            variant="contained"
                            sx={{
                                backgroundColor: "#003366",
                                "&:hover": { backgroundColor: "#002244" },
                                fontWeight: 600,
                            }}
                        >
                            Create Team
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Join Team Dialog */}
                <Dialog
                    open={joinOpen}
                    onClose={() => setJoinOpen(false)}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle sx={{ fontWeight: 700, fontSize: "1.5rem" }}>
                        🤝 Join Existing Team
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                label="Team Code"
                                placeholder="Enter 6-character team code"
                                value={joinCode}
                                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                                inputProps={{ style: { textTransform: "uppercase" } }}
                                autoFocus
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        "&.Mui-focused fieldset": {
                                            borderColor: "#C1272D",
                                        },
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: "#C1272D",
                                    },
                                }}
                            />
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Ask your team leader for the 6-character team code to join.
                            </Alert>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setJoinOpen(false)} sx={{ color: "#666" }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleJoinTeam}
                            variant="contained"
                            sx={{
                                backgroundColor: "#C1272D",
                                "&:hover": { backgroundColor: "#a01f25" },
                                fontWeight: 600,
                            }}
                        >
                            Join Team
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ✅ NEW: Team Members Dialog */}
                <Dialog
                    open={membersDialogOpen}
                    onClose={handleCloseMembersDialog}
                    fullWidth
                    maxWidth="md"
                >
                    <DialogTitle
                        sx={{
                            fontWeight: 700,
                            fontSize: "1.5rem",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            pb: 1,
                        }}
                    >
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                👥 {selectedTeam?.teamName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Team Members
                            </Typography>
                        </Box>
                        <IconButton onClick={handleCloseMembersDialog}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ p: 0 }}>
                        {loadingMembers ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    py: 8,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : teamMembers.length === 0 ? (
                            <Box sx={{ textAlign: "center", py: 8 }}>
                                <PeopleIcon sx={{ fontSize: 60, color: "#ccc", mb: 2 }} />
                                <Typography color="text.secondary">
                                    No members in this team yet
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ py: 0 }}>
                                {teamMembers.map((member, index) => {
                                    const isLeader = member.id === selectedTeam?.leaderId;
                                    return (
                                        <React.Fragment key={member.id}>
                                            <ListItem
                                                sx={{
                                                    py: 2,
                                                    backgroundColor: isLeader
                                                        ? "rgba(193, 39, 45, 0.05)"
                                                        : "transparent",
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar
                                                        sx={{
                                                            width: 50,
                                                            height: 50,
                                                            backgroundColor: isLeader
                                                                ? "#C1272D"
                                                                : "#003366",
                                                            fontSize: "1.2rem",
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {member.firstName?.charAt(0) || "?"}
                                                        {member.lastName?.charAt(0) || ""}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body1"
                                                                sx={{ fontWeight: 600 }}
                                                            >
                                                                {member.firstName} {member.lastName}
                                                            </Typography>
                                                            {isLeader && (
                                                                <Chip
                                                                    icon={<TrophyIcon />}
                                                                    label="Leader"
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: "#C1272D",
                                                                        color: "white",
                                                                        fontWeight: 600,
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                                            {member.email && (
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: 0.5,
                                                                    }}
                                                                >
                                                                    <EmailIcon
                                                                        sx={{ fontSize: 14 }}
                                                                    />
                                                                    <Typography variant="body2">
                                                                        {member.email}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            {member.college && (
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: 0.5,
                                                                    }}
                                                                >
                                                                    <SchoolIcon
                                                                        sx={{ fontSize: 14 }}
                                                                    />
                                                                    <Typography variant="body2">
                                                                        {member.college}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            {member.studentId && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                >
                                                                    ID: {member.studentId}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    }
                                                />
                                            </ListItem>
                                            {index < teamMembers.length - 1 && <Divider />}
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        )}
                    </DialogContent>
                    <Divider />
                    <DialogActions sx={{ p: 2 }}>
                        <Button
                            onClick={handleCloseMembersDialog}
                            variant="contained"
                            sx={{
                                backgroundColor: "#003366",
                                "&:hover": { backgroundColor: "#002244" },
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </AdminLayout>
    );
}
